import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  AppWindow,
  Globe,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  RefreshCw,
  Search,
  Shield,
  X,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { todayLocal } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';

function StaffUsageDetailPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialDate = searchParams.get('date') || todayLocal();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartTotals, setChartTotals] = useState(null);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [actionLoading, setActionLoading] = useState({});
  const [historyModal, setHistoryModal] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | unlisted | allow | block
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');

  const fetchChartData = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/usage-reports/staff/${staffId}/chart-data?days=7`,
        { withCredentials: true }
      );
      setChartData(res.data.chart_data || []);
      setChartTotals(res.data.totals || null);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchData = useCallback(
    async ({ soft = false } = {}) => {
      if (!staffId || !selectedDate) return;
      if (soft) setRefreshing(true);
      else setLoading(true);
      setLoadError('');
      try {
        const usageRes = await axios.get(
          `${API}/admin/usage-reports/staff/${staffId}?date=${selectedDate}`,
          { withCredentials: true }
        );
        setData(usageRes.data);
        await fetchChartData();
      } catch (error) {
        console.error('Error fetching staff usage:', error);
        setLoadError('Failed to load usage data');
        toast.error('Failed to load staff usage data');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staffId, selectedDate]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setSearchParams({ date: newDate });
  };

  const itemKey = (app) =>
    app.normalized_domain ? `url:${app.normalized_domain}` : `app:${app.app_name}`;

  const handleAddToPolicy = async (app, action) => {
    if (!data?.policy_id) {
      toast.error('Assign a usage policy to this staff member first.');
      return;
    }

    const key = itemKey(app);
    setActionLoading((prev) => ({ ...prev, [key]: true }));

    try {
      const isUrl = app.entry_type === 'url' || !!app.normalized_domain;
      await axios.post(
        `${API}/admin/usage-policies/${data.policy_id}/apps`,
        isUrl
          ? { rule_type: 'url', site_url: app.normalized_domain, app_action: action }
          : { rule_type: 'application', app_name: app.app_name, app_action: action },
        { withCredentials: true }
      );
      toast.success(
        `${isUrl ? app.normalized_domain : app.app_name} → ${action === 'block' ? 'Blocked' : 'Allowed'}`
      );
      await fetchData({ soft: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const openHistory = async (app) => {
    setHistoryModal(app);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (app.entry_type === 'url' && app.normalized_domain) {
        params.append('domain', app.normalized_domain);
      } else {
        params.append('app_name', app.app_name);
      }
      const res = await axios.get(
        `${API}/admin/usage-reports/staff/${staffId}/events?${params}`,
        { withCredentials: true }
      );
      setHistoryData(res.data.events || []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fmtTime = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const apps = data?.by_app || [];
  const counts = useMemo(() => {
    const c = { all: apps.length, unlisted: 0, allow: 0, block: 0 };
    apps.forEach((a) => {
      const s = a.policy_status || 'unlisted';
      if (s === 'allow') c.allow += 1;
      else if (s === 'block') c.block += 1;
      else c.unlisted += 1;
    });
    return c;
  }, [apps]);

  const filteredApps = useMemo(() => {
    return apps.filter((app) => {
      const status = app.policy_status || 'unlisted';
      if (filter === 'unlisted' && status !== 'unlisted') return false;
      if (filter === 'allow' && status !== 'allow') return false;
      if (filter === 'block' && status !== 'block') return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (app.app_name || '').toLowerCase().includes(q) ||
        (app.normalized_domain || '').toLowerCase().includes(q) ||
        (app.browsers || []).join(' ').toLowerCase().includes(q)
      );
    });
  }, [apps, filter, search]);

  if (loading && !data) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        Loading usage…
      </div>
    );
  }

  if (loadError && !data) {
    return (
      <div className="rounded-3xl border border-dashed border-rose-200 bg-rose-50/40 px-6 py-16 text-center">
        <p className="font-semibold text-rose-700">{loadError}</p>
        <Button
          onClick={() => fetchData()}
          className="mt-4 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700"
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="relative pb-8" data-testid="staff-usage-detail-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className={cn('relative space-y-5', refreshing && 'opacity-80')}>
        {/* Header */}
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 hover:bg-white/25"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <h1 className="truncate text-xl font-bold tracking-tight sm:text-2xl">
                    {data?.staff?.name || 'Staff'} · Usage
                  </h1>
                  <p className="mt-0.5 truncate text-sm text-indigo-50/90">
                    {data?.staff?.email}
                    {data?.staff?.department ? ` · ${data.staff.department}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <div className="flex h-10 items-center gap-2 rounded-xl bg-white/15 px-3 ring-1 ring-white/25">
                  <Calendar className="h-4 w-4" />
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="h-8 w-[9.5rem] border-0 bg-transparent p-0 text-sm text-white [color-scheme:dark] focus-visible:ring-0"
                  />
                </div>
                <Button
                  onClick={() => fetchData({ soft: true })}
                  disabled={refreshing}
                  className="h-10 rounded-xl bg-white px-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* No policy banner — very visible */}
        {!data?.policy_id && (
          <div className="att-fade-up flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 text-amber-900">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">No usage policy assigned</p>
                <p className="text-sm text-amber-800/80">
                  Allow / Block buttons stay hidden until a policy is assigned to this staff member.
                </p>
              </div>
            </div>
            <Button
              className="h-10 shrink-0 rounded-xl bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate('/admin/usage-policy')}
            >
              <Shield className="mr-1.5 h-4 w-4" />
              Assign policy
            </Button>
          </div>
        )}

        {/* KPIs */}
        <section className="att-fade-up grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            {
              label: 'Total',
              value: data?.summary?.total_formatted || '0s',
              soft: 'bg-sky-50 text-sky-700',
              icon: Clock,
            },
            {
              label: 'Allowed',
              value: data?.summary?.allowed_formatted || '0s',
              soft: 'bg-indigo-50 text-indigo-700',
              icon: CheckCircle,
            },
            {
              label: 'Blocked',
              value: data?.summary?.blocked_formatted || '0s',
              soft: 'bg-rose-50 text-rose-700',
              icon: XCircle,
            },
            {
              label: 'Unlisted',
              value: data?.summary?.unlisted_formatted || '0s',
              soft: 'bg-amber-50 text-amber-700',
              icon: AlertCircle,
            },
          ].map((k) => (
            <div
              key={k.label}
              className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', k.soft)}>
                  <k.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xl font-bold tabular-nums text-slate-900">{k.value}</p>
                  <p className="text-xs font-medium text-slate-500">{k.label}</p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Chart */}
        <section className="att-fade-up rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-bold text-slate-900">Last 7 days</h3>
              <p className="text-sm text-slate-500">Minutes by policy status</p>
            </div>
            {chartTotals && (
              <div className="flex flex-wrap gap-3 text-xs font-medium text-slate-600">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-indigo-500" />
                  Allowed {chartTotals.allowed_hours}h
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-rose-500" />
                  Blocked {chartTotals.blocked_hours}h
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />
                  Unlisted {chartTotals.unlisted_hours}h
                </span>
              </div>
            )}
          </div>
          {chartData.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No chart data yet</p>
          ) : (
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: '1px solid #e2e8f0',
                      fontSize: 12,
                    }}
                    formatter={(value, name) => {
                      const label =
                        name === 'allowed' ? 'Allowed' : name === 'blocked' ? 'Blocked' : 'Unlisted';
                      return [`${value} min`, label];
                    }}
                  />
                  <Legend
                    formatter={(value) =>
                      value === 'allowed'
                        ? 'Allowed'
                        : value === 'blocked'
                          ? 'Blocked'
                          : 'Unlisted'
                    }
                  />
                  <Bar dataKey="allowed" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="blocked" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="unlisted" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Apps / sites control */}
        <section className="att-fade-up space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Apps & websites · {selectedDate}</h3>
              <p className="text-sm text-slate-500">
                Use <span className="font-semibold text-rose-600">Block</span> /{' '}
                <span className="font-semibold text-indigo-600">Allow</span> on unlisted items to
                update the policy
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search app or site…"
                className="h-9 w-full rounded-xl border-slate-200 pl-8 text-sm sm:w-56"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 rounded-2xl bg-slate-100 p-1">
            {[
              { id: 'all', label: 'All', n: counts.all },
              { id: 'unlisted', label: 'Needs review', n: counts.unlisted },
              { id: 'allow', label: 'Allowed', n: counts.allow },
              { id: 'block', label: 'Blocked', n: counts.block },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setFilter(t.id)}
                className={cn(
                  'rounded-xl px-3 py-2 text-sm font-semibold transition',
                  filter === t.id
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                )}
              >
                {t.label}
                <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-slate-500">
                  {t.n}
                </span>
              </button>
            ))}
          </div>

          {filteredApps.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-14 text-center text-slate-500">
              {apps.length === 0
                ? 'No usage recorded for this date'
                : search
                  ? `No matches for “${search}”`
                  : 'Nothing in this filter'}
            </div>
          ) : (
            <div className="space-y-2.5">
              {filteredApps.map((app, index) => {
                const isUrlEntry = app.entry_type === 'url' || !!app.normalized_domain;
                const key = itemKey(app);
                const isLoading = actionLoading[key];
                const status = app.policy_status || 'unlisted';
                const title = isUrlEntry
                  ? app.normalized_domain
                  : app.app_name;
                const subtitle = isUrlEntry
                  ? app.browsers?.join(', ') || app.app_name
                  : null;

                return (
                  <div
                    key={index}
                    data-testid={`usage-row-${index}`}
                    className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div
                        className={cn(
                          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                          isUrlEntry ? 'bg-sky-50 text-sky-600' : 'bg-violet-50 text-violet-600'
                        )}
                      >
                        {isUrlEntry ? (
                          <Globe className="h-5 w-5" />
                        ) : (
                          <AppWindow className="h-5 w-5" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {isUrlEntry ? (
                            <a
                              href={`https://${app.normalized_domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate font-semibold text-sky-700 hover:underline"
                            >
                              {title}
                            </a>
                          ) : (
                            <p className="truncate font-semibold text-slate-900">{title}</p>
                          )}
                          {status === 'block' && (
                            <Badge className="bg-rose-50 text-rose-700 ring-1 ring-rose-100">
                              Blocked
                            </Badge>
                          )}
                          {status === 'allow' && (
                            <Badge className="bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                              Allowed
                            </Badge>
                          )}
                          {status === 'unlisted' && (
                            <Badge className="bg-amber-50 text-amber-700 ring-1 ring-amber-100">
                              Unlisted
                            </Badge>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {subtitle ? `${subtitle} · ` : ''}
                          {app.total_formatted}
                          {app.record_count > 1 ? ` · ${app.record_count} sessions` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 rounded-xl border-slate-200 text-xs"
                        onClick={() => openHistory(app)}
                        data-testid={`view-history-btn-${index}`}
                      >
                        Sessions
                      </Button>

                      {data?.policy_id && status === 'unlisted' && (
                        <>
                          <Button
                            size="sm"
                            className="h-9 rounded-xl bg-indigo-600 text-xs font-semibold hover:bg-indigo-700"
                            disabled={isLoading}
                            onClick={() => handleAddToPolicy(app, 'allow')}
                            data-testid={`allow-btn-${index}`}
                          >
                            {isLoading ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <CheckCircle className="mr-1 h-3.5 w-3.5" />
                            )}
                            Allow
                          </Button>
                          <Button
                            size="sm"
                            className="h-9 rounded-xl bg-rose-600 text-xs font-semibold hover:bg-rose-700"
                            disabled={isLoading}
                            onClick={() => handleAddToPolicy(app, 'block')}
                            data-testid={`block-btn-${index}`}
                          >
                            {isLoading ? (
                              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <XCircle className="mr-1 h-3.5 w-3.5" />
                            )}
                            Block
                          </Button>
                        </>
                      )}

                      {data?.policy_id && status === 'allow' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 rounded-xl border-rose-200 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                          disabled={isLoading}
                          onClick={() => handleAddToPolicy(app, 'block')}
                        >
                          Switch to Block
                        </Button>
                      )}

                      {data?.policy_id && status === 'block' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 rounded-xl border-indigo-200 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                          disabled={isLoading}
                          onClick={() => handleAddToPolicy(app, 'allow')}
                        >
                          Switch to Allow
                        </Button>
                      )}

                      {!data?.policy_id && status === 'unlisted' && (
                        <span className="text-xs text-amber-700">Assign policy to control</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* History modal */}
      {historyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setHistoryModal(null)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  {historyModal.entry_type === 'url' ? (
                    <>
                      <Globe className="h-5 w-5 text-sky-600" />
                      {historyModal.normalized_domain}
                    </>
                  ) : (
                    <>
                      <AppWindow className="h-5 w-5 text-violet-600" />
                      {historyModal.app_name}
                    </>
                  )}
                </h2>
                <p className="text-sm text-slate-500">Sessions on {selectedDate}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setHistoryModal(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex-1 overflow-auto p-5">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
              ) : historyData.length === 0 ? (
                <div className="py-12 text-center text-slate-400">No sessions for this date</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    <tr>
                      <th className="rounded-l-lg px-3 py-2 text-left">App</th>
                      <th className="px-3 py-2 text-left">URL</th>
                      <th className="px-3 py-2 text-left">Start</th>
                      <th className="px-3 py-2 text-left">End</th>
                      <th className="rounded-r-lg px-3 py-2 text-left">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {historyData.map((ev, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2.5 font-medium text-slate-700">{ev.app_name}</td>
                        <td className="px-3 py-2.5">
                          {ev.site_url ? (
                            <a
                              href={
                                ev.site_url.startsWith('http')
                                  ? ev.site_url
                                  : `https://${ev.site_url}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block max-w-[180px] truncate text-sky-600 hover:underline"
                            >
                              {ev.site_url}
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-slate-600">
                          {fmtTime(ev.start_time)}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-slate-600">
                          {fmtTime(ev.end_time)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge className="bg-sky-50 text-sky-700 text-xs">
                            {ev.duration_formatted}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm text-slate-500">
              <span>
                {historyData.length} session{historyData.length !== 1 ? 's' : ''}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={() => setHistoryModal(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffUsageDetailPage;
